<!--
  The sign-in portal: sign in with your name and a passkey, or create an account: your full name,
  which groups you're in (open groups let you in right away; saying you're someone already there
  waits for the treasurer), then a passkey. First-time setup (the treasurer's account) is here too.
-->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import FullName from "../components/FullName.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { member } from "../lib/member.svelte.ts";
  import { api, NetworkError } from "../lib/api.ts";
  import { colorOf } from "../lib/colors.ts";
  import { devSignIn, passkeyError, passkeysSupported, registerPasskey, signInWithPasskey } from "../lib/auth.ts";

  let { inline = false }: { inline?: boolean } = $props();

  interface Match {
    groupId: string;
    groupName: string;
    personId: string;
    name: string;
    color: string;
    taken: boolean;
  }
  interface Joinable {
    groupId: string;
    name: string;
    joining: "open" | "ask";
    matches: Match[];
  }

  let tab = $state<"in" | "up">("in");
  let step = $state<"details" | "groups" | "create">("details");
  let name = $state("");
  let username = $state("");
  let code = $state("");
  let busy = $state(false);
  let error = $state("");
  let taken = $state<string | null>(null);
  let groups = $state<Joinable[]>([]);
  /** groupId -> "new" (join as a new person) or the id of the person who is you. */
  let chosen = $state<Record<string, string>>({});
  const supported = passkeysSupported();
  const dev = import.meta.env.DEV;

  const fullName = $derived(name.trim().replace(/\s+/g, " "));
  const hasLast = $derived(fullName.split(" ").length >= 2);
  const joins = $derived(
    Object.entries(chosen).map(([groupId, v]) => ({ groupId, personId: v === "new" ? null : v })),
  );
  /** Groups they'll see as soon as the account is made, and ones the treasurer confirms first. */
  const rightAway = $derived(
    joins.filter((j) => !j.personId && groups.find((g) => g.groupId === j.groupId)?.joining === "open").map((j) => groups.find((g) => g.groupId === j.groupId)!.name),
  );
  const toConfirm = $derived(
    joins.filter((j) => j.personId || groups.find((g) => g.groupId === j.groupId)?.joining === "ask").map((j) => groups.find((g) => g.groupId === j.groupId)?.name ?? ""),
  );

  async function run(fn: () => Promise<void>) {
    busy = true;
    error = "";
    try {
      await fn();
    } catch (e) {
      error = e instanceof NetworkError ? "You're offline. Try again when you're connected." : passkeyError(e);
    } finally {
      busy = false;
    }
  }

  const signIn = () =>
    run(async () => {
      await signInWithPasskey(username);
      member.clear();
      await app.refreshAccount();
    });

  const toGroups = () =>
    run(async () => {
      taken = null;
      if (!hasLast) {
        error = "Add your last name too.";
        return;
      }
      const r = await api<{ username: { free: boolean; display: string; problem?: string } | null; groups: Joinable[] }>("/api/auth/lookup", {
        body: { name: fullName, username: fullName, groups: true },
      });
      if (r.username?.problem) {
        error = r.username.problem;
        return;
      }
      if (!r.username?.free) {
        taken = r.username?.display ?? fullName;
        return;
      }
      name = r.username.display;
      groups = r.groups ?? [];
      // Keep earlier picks that still make sense.
      const keep: Record<string, string> = {};
      for (const g of groups) {
        const v = chosen[g.groupId];
        if (v === "new" || g.matches.some((m) => m.personId === v && !m.taken)) keep[g.groupId] = v!;
      }
      chosen = keep;
      step = "groups";
    });

  function toggleGroup(g: Joinable) {
    if (chosen[g.groupId]) {
      delete chosen[g.groupId];
      return;
    }
    // Someone with your name who has no account yet is most likely you.
    const free = g.matches.filter((m) => !m.taken);
    chosen[g.groupId] = free.length === 1 ? free[0]!.personId : "new";
  }

  const create = () =>
    run(async () => {
      await registerPasskey({ signup: true, name: fullName, joins });
      member.clear();
      await app.refreshAccount();
    });

  function switchTab(t: "in" | "up") {
    tab = t;
    step = "details";
    error = "";
    taken = null;
  }
</script>

<div class:screen={!inline}>
  <div class="card">
    <div class="brand">
      <svg viewBox="0 0 64 64" width="44" height="44" aria-hidden="true" class="mark">
        <rect width="64" height="64" rx="14" fill="currentColor" />
        <circle cx="24" cy="32" r="11" fill="#e5484d" />
        <path d="M40 21a11 11 0 0 1 0 22z" fill="#3b7ddd" />
        <circle cx="40" cy="32" r="10" fill="none" stroke="#3b7ddd" stroke-width="2" />
      </svg>
      <div>
        <p class="product">Split Ledger</p>
        {#if H.SIGNIN_TAGLINE.trim()}<p class="muted small">{H.SIGNIN_TAGLINE}</p>{/if}
      </div>
    </div>

    {#if app.needsSetup}
      <h1>Set up Split Ledger</h1>
      {#if H.SIGNIN_SETUP_NOTE.trim()}<p class="muted">{H.SIGNIN_SETUP_NOTE}</p>{/if}
      <form onsubmit={(e) => { e.preventDefault(); void run(async () => { await registerPasskey({ name, setupToken: code }); await app.refreshAccount(); }); }}>
        <FullName bind:value={name} label="Your name" />
        <label class="field">
          <span>Setup code</span>
          <input class="input" bind:value={code} autocomplete="off" />
          <span class="hint">The SETUP_TOKEN you set when deploying. It only works once.</span>
        </label>
        <button class="btn primary" disabled={busy || !supported || !code || !name.trim().includes(" ")}>Create passkey</button>
      </form>
    {:else}
      <div class="tabs" role="tablist">
        <button role="tab" aria-selected={tab === "in"} onclick={() => switchTab("in")}>Sign in</button>
        <button role="tab" aria-selected={tab === "up"} onclick={() => switchTab("up")}>Create account</button>
      </div>

      {#if tab === "in"}
        <form onsubmit={(e) => { e.preventDefault(); void signIn(); }}>
          <label class="field">
            <span>Your name</span>
            <input class="input" bind:value={username} autocomplete="username webauthn" autocapitalize="words" spellcheck="false" placeholder="Emily Chen" />
            <span class="hint">The name you made your account with. Or leave it empty and pick a passkey.</span>
          </label>
          <button class="btn primary" disabled={busy || !supported}>Sign in with passkey</button>
        </form>
        <p class="alt muted">New here? <button class="link" onclick={() => switchTab("up")}>Create an account</button></p>
      {:else if step === "details"}
        <form onsubmit={(e) => { e.preventDefault(); void toGroups(); }}>
          <label class="field">
            <span>Full name</span>
            <input class="input" bind:value={name} oninput={() => (taken = null)} autocomplete="name" autocapitalize="words" spellcheck="false" placeholder="Emily Chen" />
          </label>
          {#if taken}
            <p class="hint bad">
              {taken} already has an account. <button type="button" class="link" onclick={() => { username = taken ?? ""; switchTab("in"); }}>Sign in instead</button>
            </p>
          {/if}
          <button class="btn primary" disabled={busy || !hasLast}>Continue</button>
        </form>
      {:else if step === "groups"}
        <div class="step">
          <h2>Which groups are you in?</h2>
          {#if groups.length === 0}
            <p class="muted">There aren't any groups to join here yet. Make your account now, and open the invite link the treasurer sends you.</p>
          {:else}
            {#if H.SIGNIN_GROUPS_NOTE.trim()}<p class="muted">{H.SIGNIN_GROUPS_NOTE}</p>{/if}
            {#each groups as g (g.groupId)}
              {@const on = !!chosen[g.groupId]}
              <div class="group" class:on>
                <button type="button" class="gpick" aria-pressed={on} onclick={() => toggleGroup(g)}>
                  <span class="check" aria-hidden="true">{on ? "✓" : ""}</span>
                  <span class="gname">{g.name}</span>
                  <span class="muted small">{g.joining === "open" ? "Join right away" : "The treasurer confirms"}</span>
                </button>
                {#if on && g.matches.length}
                  <div class="who">
                    <p class="small muted">Someone with your name is already in {g.name}. Is that you?</p>
                    {#each g.matches as m (m.personId)}
                      <button type="button" class="person" class:on={chosen[g.groupId] === m.personId} disabled={m.taken} aria-pressed={chosen[g.groupId] === m.personId} onclick={() => (chosen[g.groupId] = m.personId)} style:--c={colorOf(m.color)}>
                        <span class="dot" aria-hidden="true"></span>
                        <span class="pname">{m.name}</span>
                        <span class="muted small">{m.taken ? "Already has an account" : chosen[g.groupId] === m.personId ? "That's me" : "Tap if this is you"}</span>
                      </button>
                    {/each}
                    <button type="button" class="person" class:on={chosen[g.groupId] === "new"} aria-pressed={chosen[g.groupId] === "new"} onclick={() => (chosen[g.groupId] = "new")} style:--c="var(--muted)">
                      <span class="dot" aria-hidden="true"></span>
                      <span class="pname">None of these, I'm new</span>
                      <span class="muted small">Join as {fullName}</span>
                    </button>
                  </div>
                {/if}
              </div>
            {/each}
          {/if}
          <div class="row">
            <button class="btn quiet" onclick={() => (step = "details")}>Back</button>
            <button class="btn primary" onclick={() => (step = "create")}>{joins.length || groups.length === 0 ? "Continue" : "Skip for now"}</button>
          </div>
        </div>
      {:else}
        <div class="step">
          <h2>Create your passkey</h2>
          {#if H.SIGNIN_PASSKEY_NOTE.trim()}<p class="muted">{H.SIGNIN_PASSKEY_NOTE}</p>{/if}
          <dl class="summary">
            <div><dt>Full name</dt><dd>{fullName}</dd></div>
            {#if rightAway.length}<div><dt>Joining now</dt><dd>{rightAway.join(", ")}</dd></div>{/if}
            {#if toConfirm.length}<div><dt>Treasurer confirms</dt><dd>{toConfirm.join(", ")}</dd></div>{/if}
          </dl>
          <div class="row">
            <button class="btn quiet" onclick={() => (step = "groups")}>Back</button>
            <button class="btn primary" disabled={busy || !supported} onclick={create}>Create account</button>
          </div>
        </div>
      {/if}
    {/if}

    {#if !supported}<p class="owes">This browser doesn't support passkeys. Try Safari, Chrome or Edge.</p>{/if}
    {#if error}<p class="owes" role="alert">{error}</p>{/if}
    {#if dev}<button class="btn quiet small" onclick={() => run(async () => { await devSignIn(); await app.refreshAccount(); })}>Developer sign-in (localhost only)</button>{/if}
  </div>
</div>

<style>
  .screen {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 24px var(--gutter);
  }

  .card {
    width: min(440px, 100%);
    display: grid;
    gap: 16px;
  }

  .screen .card {
    padding: 28px;
    border-radius: var(--r-lg);
    background: var(--surface);
    border: 1px solid var(--rule);
  }

  .brand {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .brand p {
    margin: 0;
  }

  .product {
    font-weight: 700;
    font-size: var(--t-lg);
  }

  .mark {
    color: var(--ink);
  }

  h1,
  h2 {
    margin: 0;
  }

  h2 {
    font-size: var(--t-xl);
  }

  .small {
    font-size: var(--t-sm);
  }

  .tabs {
    display: flex;
    gap: 2px;
    padding: 3px;
    border-radius: 12px;
    background: var(--wash);
  }

  .tabs button {
    flex: 1;
    padding: 9px 12px;
    border: 0;
    border-radius: 9px;
    background: none;
    color: var(--ink-2);
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }

  .tabs button[aria-selected="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.15);
  }

  form,
  .step {
    display: grid;
    gap: 14px;
  }

  .step p {
    margin: 0;
  }

  .hint.ok {
    color: var(--paid, #2f8a4c);
  }

  .hint.bad {
    color: var(--owes, #c2410c);
  }

  .alt {
    margin: 0;
    font-size: var(--t-sm);
  }

  .link {
    padding: 0;
    border: 0;
    background: none;
    color: var(--focus, #3b7ddd);
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }

  .group {
    display: grid;
    gap: 8px;
    border: 1px solid var(--rule);
    border-radius: var(--r-md);
    background: var(--paper);
  }

  .group.on {
    border-color: var(--focus, #3b7ddd);
  }

  .gpick {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 0;
    background: none;
    color: var(--ink);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .check {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 1.5px solid var(--rule);
    display: grid;
    place-items: center;
    font-size: 13px;
    font-weight: 700;
  }

  .group.on .check {
    background: var(--focus, #3b7ddd);
    border-color: var(--focus, #3b7ddd);
    color: #fff;
  }

  .gname {
    font-weight: 650;
  }

  .who {
    display: grid;
    gap: 8px;
    padding: 0 12px 12px;
  }

  .who p {
    margin: 0;
  }

  .person {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: var(--r-md);
    border: 1px solid var(--rule);
    background: var(--paper);
    color: var(--ink);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .person.on {
    border-color: var(--c);
    box-shadow: inset 0 0 0 1px var(--c);
  }

  .person:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--c);
  }

  .pname {
    font-weight: 600;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }

  .summary {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin: 0;
    padding: 12px 14px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .summary dt {
    font-size: var(--t-xs);
    color: var(--muted);
  }

  .summary dd {
    margin: 0;
    font-weight: 600;
  }
</style>
